/**
 * Static homepage data for UKSS theme.
 * All homepage content lives here, never hardcoded in components.
 *
 * Types are defined inline — this file is self-contained.
 *
 * TODO: Most hrefs in this file point to /store as a placeholder.
 * Update them when Medusa collections/categories are configured.
 */

// ─── Inline Types ───────────────────────────────────────────────────────────

export interface PromoBanner {
  id: string;
  title: string;
  subtitle: string;
  image: string;
  href: string;
  /** Tailwind background color class */
  bgColor: string;
  /** Whether text should be dark (for light backgrounds) */
  darkText?: boolean;
}

export interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export interface CollectionCard {
  id: string;
  name: string;
  description: string;
  image: string;
  href: string;
}

// ─── Hero Section ────────────────────────────────────────────────────────────

export const HOMEPAGE_HERO = {
  heading: 'Find The Kit.\nMake it Yours.',
  subtitle:
    'Since 2004 we\u2019ve helped supporters find authentic kits online, with full personalisation options and fast, competitive worldwide shipping.',
  trustBadges: [
    'Served over one million supporters',
    'Worldwide shipping',
    'Established 2004',
  ],
  ctaPrimary: { label: 'Shop Best Sellers', href: '/store' },
  ctaSecondary: { label: 'Shop By Team', href: '/store' },
  heroImage: '/images/header-right-img.png',
};

// ─── Trust Bar (below hero) ─────────────────────────────────────────────────

export const TRUST_BAR = {
  customerStat: {
    prefix: 'Over',
    count: '2,000,000',
    suffix: 'customers since 2004',
  },
  payLater: {
    label: 'Buy now, pay later with',
    providers: [
      { id: 'klarna', name: 'Klarna', logo: '/images/payment-icon/Klarna.png', pillBg: 'bg-[#ffb3c7]' },
      { id: 'clearpay', name: 'Clearpay', logo: '/images/payment-icon/Clearpay.png', pillBg: 'bg-[#b2fce4]' },
      { id: 'paypal', name: 'PayPal', logo: '/images/payment-icon/Paypal.png', pillBg: 'bg-[#6ba3e8]' },
    ],
  },
};


// ─── Mini Banners (below Shop Teams) ─────────────────────────────────────────

export const MINI_BANNERS = [
  {
    id: 'january-sale-banner',
    image: '/images/mini-banner-1.png',
    alt: 'January Sale \u2014 Up to 70% off 2025/26 kits',
    href: '/store',
  },
  {
    id: 'kids-kits-banner',
    image: '/images/mini-banner-2.png',
    alt: 'Kids Mini Kits & Football Shirts \u2014 Shop kids sizes',
    href: '/store',
  },
];

// ─── Personalisation Mini Banners (below PersonalisedShirts) ────────────────

export const PERSONALISATION_MINI_BANNERS = [
  {
    id: 'vintage-collection',
    image: '/images/Vintage Collection.png',
    mobileImage: '/images/Vintage Collection Mobile.png',
    alt: 'Vintage Collection \u2014 Classic retro football shirts',
    href: '/store',
  },
  {
    id: 'kids-mini-kits',
    image: '/images/Kids Mini Kits.png',
    mobileImage: '/images/Kids Mini Kits Mobile.png',
    alt: 'Kids Mini Kits \u2014 Junior sizes for young supporters',
    href: '/store',
  },
];

// ─── Popular Products Section ───────────────────────────────────────────────

export const POPULAR_PRODUCTS_SECTION = {
  heading: 'Popular Right Now',
  description: 'The kits everyone\u2019s wearing this season.',
};

// ─── Categories (shop by country) ───────────────────────────────────────────

export interface CategoryCard {
  id: string;
  name: string;
  image: string;
  href: string;
}

export const HOMEPAGE_CATEGORIES: CategoryCard[] = [
  { id: 'england', name: 'England', image: '/images/homepage/categories/england.jpg', href: '/categories/ukss-829' },
  { id: 'scotland', name: 'Scotland', image: '/images/homepage/categories/scotland.jpg', href: '/categories/ukss-338' },
  { id: 'brazil', name: 'Brazil', image: '/images/homepage/categories/brazil.jpg', href: '/categories/ukss-355' },
  { id: 'argentina', name: 'Argentina', image: '/images/homepage/categories/argentina.jpg', href: '/categories/ukss-353' },
  { id: 'portugal', name: 'Portugal', image: '/images/homepage/categories/portugal.jpg', href: '/categories/ukss-334' },
  { id: 'germany', name: 'Germany', image: '/images/homepage/categories/germany.jpg', href: '/categories/ukss-325' },
];

// ─── Club Teams ──────────────────────────────────────────────────────────────

export interface ClubTeamLogo {
  id: string;
  name: string;
  image: string;
  href: string;
}

export interface ClubTeamFeatured {
  id: string;
  name: string;
  image: string;
  href: string;
}

export const CLUB_TEAMS_SECTION = {
  heading: 'Club Teams',
  viewAllLabel: 'View All',
  viewAllHref: '/store',
};

export const CLUB_TEAMS_LOGOS: ClubTeamLogo[] = [
  { id: 'man-city', name: 'Man City', image: '/images/homepage/club-teams/man-city.jpg', href: '/categories/ukss-201' },
  { id: 'chelsea', name: 'Chelsea', image: '/images/homepage/club-teams/chelsea.jpg', href: '/categories/ukss-196' },
  { id: 'bayern-munich', name: 'Bayern Munich', image: '/images/homepage/club-teams/bayern-munich.jpg', href: '/categories/ukss-172' },
  { id: 'man-utd', name: 'Man Utd', image: '/images/homepage/club-teams/man-utd.jpg', href: '/categories/ukss-202' },
  { id: 'liverpool', name: 'Liverpool', image: '/images/homepage/club-teams/liverpool.jpg', href: '/categories/ukss-200' },
  { id: 'atletico-madrid', name: 'Atletico Madrid', image: '/images/homepage/club-teams/atletico-madrid.jpg', href: '/results/Atletico%20Madrid' },
  { id: 'juventus', name: 'Juventus', image: '/images/homepage/club-teams/juventus.jpg', href: '/categories/ukss-86' },
  { id: 'borussia-dortmund', name: 'Borussia Dortmund', image: '/images/homepage/club-teams/borussia-dortmund.jpg', href: '/categories/ukss-173' },
  { id: 'red-bull-leipzig', name: 'Red Bull Leipzig', image: '/images/homepage/club-teams/red-bull-leipzig.jpg', href: '/categories/ukss-4674' },
  { id: 'arsenal', name: 'Arsenal', image: '/images/homepage/club-teams/arsenal.jpg', href: '/categories/ukss-190' },
];

export const CLUB_TEAMS_FEATURED: ClubTeamFeatured[] = [
  { id: 'real-madrid', name: 'Real Madrid', image: '/images/homepage/club-teams/real-madrid-featured.png', href: '/categories/ukss-122' },
  { id: 'barcelona', name: 'Barcelona', image: '/images/homepage/club-teams/barcelona-featured.jpg', href: '/categories/ukss-114' },
  { id: 'bayern-munich-featured', name: 'Bayern Munich', image: '/images/homepage/club-teams/bayern-munich-featured.jpg', href: '/categories/ukss-172' },
  { id: 'psg', name: 'Paris Saint Germain', image: '/images/homepage/club-teams/psg-featured.jpg', href: '/categories/ukss-53' },
];

// ─── Shop Collections ────────────────────────────────────────────────────────

export const SHOP_COLLECTIONS: CollectionCard[] = [
  {
    id: 'club-teams',
    name: 'Club Teams',
    description: 'Top leagues & cult favourites',
    image: '/images/Club Teams.png',
    href: '/store'
  },
  {
    id: 'international',
    name: 'International',
    description: 'Wear your country with pride',
    image: '/images/International.png',
    href: '/store'
  },
  {
    id: 'new-arrivals',
    name: 'New Arrivals',
    description: 'Fresh drops first',
    image: '/images/New Arrivals.png',
    href: '/store'
  },
  {
    id: 'kids',
    name: 'Kids Sizes',
    description: 'Mini kits, big energy',
    image: '/images/Kids Sizes.png',
    href: '/store'
  },
  {
    id: 'retro',
    name: 'Retro Shirts',
    description: 'Classics that never age',
    image: '/images/Retro Classics.png',
    href: '/store'
  },
  {
    id: 'gifts',
    name: 'Gifts',
    description: 'Personalised fan favourites',
    image: '/images/Gifts.png',
    href: '/store'
  },
];

// ─── Social Proof ────────────────────────────────────────────────────────────

export const SOCIAL_PROOF_HEADLINE = 'Over 2 million supporters served since 2004.';

export const TESTIMONIALS = [
  {
    id: 'testimonial-1',
    rating: 5,
    title: 'Excellent Quality',
    text: 'The shirt quality is spot on \u2014 authentic, well-made and exactly as described. Delivery was faster than expected too. Will definitely be ordering again for next season.',
    author: 'James M.',
    verified: true,
  },
  {
    id: 'testimonial-2',
    rating: 5,
    title: 'Great Personalisation',
    text: 'Had my son\'s name printed on the back of his favourite team\'s shirt. The printing quality is brilliant \u2014 crisp lettering and perfectly aligned. He absolutely loves it.',
    author: 'Sarah T.',
    verified: true,
  },
  {
    id: 'testimonial-3',
    rating: 4,
    title: 'Fast Shipping',
    text: 'Ordered from Italy and the shirt arrived in under a week. Tracking updates were helpful and packaging was secure. Great international delivery service.',
    author: 'Marco R.',
    verified: true,
  },
  {
    id: 'testimonial-4',
    rating: 5,
    title: 'Huge Selection',
    text: 'I couldn\'t find my team\'s away shirt anywhere else online. UK Soccer Shop had it in stock along with kits from leagues I didn\'t even know they covered. Impressive range.',
    author: 'Emily K.',
    verified: true,
  },
];

// ─── Company Description ────────────────────────────────────────────────────

export const COMPANY_DESCRIPTION = {
  title: 'Official Football Shirts & Kits',
  text: 'UK Soccer Shop is the world\'s leading online retailer of official football shirts, kits, and merchandise. Since 2004, we\'ve served over two million supporters worldwide, offering an unrivalled selection of authentic, licensed products from clubs and national teams across the Premier League, La Liga, Serie A, Bundesliga, Ligue 1, and international football. Every shirt we sell is 100% genuine and officially licensed \u2014 we never sell fakes. With full personalisation options, fast worldwide shipping, and a dedicated customer service team, we\'re proud to be the trusted destination for football fans everywhere. Whether you\'re looking for the latest home kit, a classic retro shirt, or personalised gear with your favourite player\'s name, UK Soccer Shop has you covered.',
};

// ─── FAQ Section ─────────────────────────────────────────────────────────────

export const FAQ_CATEGORIES = [
  { id: 'shipping', label: 'Shipping & delivery' },
  { id: 'customisation', label: 'Customisation' },
  { id: 'printing', label: 'Printing information' },
  { id: 'other', label: 'Other' },
];

export const HOMEPAGE_FAQ_ITEMS: FAQItem[] = [
  // Shipping & delivery
  {
    id: 'faq-shipping-1',
    category: 'shipping',
    question: 'Do you ship worldwide?',
    answer:
      'Yes, we ship to over 200 countries worldwide. We offer various shipping options including standard and express delivery to most destinations.',
  },
  {
    id: 'faq-shipping-2',
    category: 'shipping',
    question: 'How long does delivery take?',
    answer:
      'Standard UK delivery takes 3-5 business days. International delivery takes 5-10 business days depending on your location. Express options are available for faster delivery.',
  },
  {
    id: 'faq-shipping-3',
    category: 'shipping',
    question: 'Do you offer express shipping?',
    answer:
      'Yes, we offer express shipping options for most destinations. Express UK delivery takes 1-2 business days, and international express takes 2-5 business days.',
  },
  {
    id: 'faq-shipping-4',
    category: 'shipping',
    question: 'Can I track my order?',
    answer:
      'Yes, once your order is dispatched, you will receive a tracking number via email. You can use this to track your package in real-time through our courier partners.',
  },
  {
    id: 'faq-shipping-5',
    category: 'shipping',
    question: 'What happens if my package is lost?',
    answer:
      'If your package is lost in transit, please contact our customer service team. We will work with the courier to locate your package or arrange a replacement or refund.',
  },

  // Customisation
  {
    id: 'faq-custom-1',
    category: 'customisation',
    question: 'Can I personalise any shirt?',
    answer:
      'Most shirts in our range can be personalised with your chosen name and number. Some items, particularly retro shirts, may have limited personalisation options.',
  },
  {
    id: 'faq-custom-2',
    category: 'customisation',
    question: 'How long does personalisation take?',
    answer:
      'Personalisation is completed in our UK workshop and typically takes 1-2 business days on top of standard delivery times. All personalised shirts are made to order.',
  },
  {
    id: 'faq-custom-3',
    category: 'customisation',
    question: 'Can I return a personalised shirt?',
    answer:
      'Personalised items can be returned if there is a manufacturing defect or printing error. However, we cannot accept returns on personalised items if you simply change your mind.',
  },

  // Printing information
  {
    id: 'faq-print-1',
    category: 'printing',
    question: 'What printing method do you use?',
    answer:
      'We use official heat-pressed vinyl printing, the same method used by professional clubs. This ensures authentic, durable printing that lasts.',
  },
  {
    id: 'faq-print-2',
    category: 'printing',
    question: 'Are the prints authentic?',
    answer:
      'Yes, all our printing uses official licensed fonts and numbers. We are authorised to print official player names and numbers for all major leagues and teams.',
  },
  {
    id: 'faq-print-3',
    category: 'printing',
    question: 'How do I care for printed shirts?',
    answer:
      'Turn shirts inside out before washing, use cold water, and avoid tumble drying. This will help preserve the printing and keep your shirt looking great for years.',
  },

  // Other
  {
    id: 'faq-other-1',
    category: 'other',
    question: 'Are all your shirts authentic and officially licensed?',
    answer:
      'Yes, every football shirt we sell is 100% authentic and officially licensed by the clubs and brands. We are authorised retailers for all major football kit manufacturers.',
  },
  {
    id: 'faq-other-2',
    category: 'other',
    question: 'What is your returns policy?',
    answer:
      'We offer a 30-day money-back guarantee on all unworn items in original packaging with tags attached. Personalised items can be returned if there is a defect.',
  },
  {
    id: 'faq-other-3',
    category: 'other',
    question: 'Do you have a physical store?',
    answer:
      'We are primarily an online retailer, but you can visit our warehouse in the UK by appointment. Please contact us to arrange a visit.',
  },
];

// ─── Info Banner ─────────────────────────────────────────────────────────────

export const INFO_BANNER_TEXT =
  'Football shirts for clubs & countries worldwide. Personalised in minutes.';

// ─── Backwards Compatibility ────────────────────────────────────────────────
// These exports preserve compatibility with the existing TrustBar component
// until it is updated to use the new TRUST_BAR structure in Task 4.

export type TrustBarIcon = 'users' | 'truck' | 'shield' | 'refresh';

export const TRUST_BAR_ITEMS: {
  icon: TrustBarIcon;
  value: string;
  label: string;
}[] = [
  { icon: 'users', value: 'Over 2,000,000', label: 'Customers since 2004' },
  { icon: 'truck', value: 'Worldwide', label: 'Fast shipping' },
  { icon: 'shield', value: '100% Authentic', label: 'Official licensed products' },
  { icon: 'refresh', value: '30-Day', label: 'Easy returns' },
];
